
import React, { useState } from 'react';
import { 
  ArrowLeft, 
  ShieldAlert, 
  MoreHorizontal, 
  Eye, 
  ExternalLink, 
  Flag, 
  CheckCircle, 
  History, 
  User, 
  FileText, 
  MessageSquare, 
  Clock, 
  ShieldCheck,
  Zap,
  TrendingUp,
  Search,
  Filter,
  ArrowRight,
  Download,
  Send
} from 'lucide-react';

interface CaseHistoryEntry {
  id: string;
  date: string;
  action: string;
  user: string;
  notes?: string;
  statusChange?: string;
  type: 'system' | 'user';
}

interface AMLCase {
  id: string;
  type: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  description: string;
  targetAccount: string;
  assignedTo: string;
  lastSeen: string;
  status: 'Open' | 'In Review' | 'Flagged' | 'Resolved';
  history: CaseHistoryEntry[];
}

const MOCK_CASES: AMLCase[] = [
  {
    id: 'AML-3001',
    type: 'Structuring Pattern',
    severity: 'Critical',
    description: 'High-velocity transfers observed below reporting thresholds.',
    targetAccount: 'GB49 BARC 2039 4483 1284',
    assignedTo: 'Julian C.',
    lastSeen: '12m ago',
    status: 'In Review',
    history: [
      { id: 'h1', date: 'Oct 28, 09:00', action: 'Case Automatically Generated', user: 'Crystal API', type: 'system' },
      { id: 'h2', date: 'Oct 28, 09:15', action: 'Account Flagged for Review', user: 'System', type: 'system' },
      { id: 'h3', date: 'Oct 28, 10:30', action: 'Assigned to Officer', user: 'Julian C.', statusChange: 'In Review', type: 'user' },
      { id: 'h4', date: 'Oct 28, 11:20', action: 'Internal Note Added', user: 'Julian C.', notes: 'Checking source of funds from secondary wallet.', type: 'user' },
    ]
  },
  {
    id: 'AML-3002',
    type: 'Sanctions Nexus',
    severity: 'High',
    description: 'Possible match with EU-104-92 Consolidated List.',
    targetAccount: 'CH93 UBSW 1002 9934 5512',
    assignedTo: 'Albert H.',
    lastSeen: '1h ago',
    status: 'Open',
    history: [
      { id: 'h1', date: 'Oct 27, 14:00', action: 'Case Created', user: 'Sanctions Engine', type: 'system' },
      { id: 'h2', date: 'Oct 27, 15:00', action: 'Entity Match Confirmed (82%)', user: 'AI Validator', type: 'system' }
    ]
  }
];

export const AMLView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [selectedCase, setSelectedCase] = useState<AMLCase | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'intelligence' | 'history'>('overview');
  const [newNote, setNewNote] = useState('');

  const handleAddNote = () => {
    if (!selectedCase || !newNote.trim()) return;
    
    const entry: CaseHistoryEntry = {
      id: `h${Date.now()}`,
      date: 'Just Now',
      action: 'Officer Note Added',
      user: 'Julian C.',
      notes: newNote,
      type: 'user'
    };
    
    // In a real app we'd update state or call an API
    selectedCase.history.push(entry);
    setNewNote('');
  };

  if (selectedCase) {
    return (
      <div className="p-6 space-y-6 animate-in slide-in-from-right duration-500 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => setSelectedCase(null)} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-600">
              <ArrowLeft size={20} />
            </button>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-2xl font-black text-slate-900 tracking-tight">{selectedCase.id}</h1>
                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase border tracking-widest ${
                  selectedCase.severity === 'Critical' ? 'bg-rose-50 text-rose-600 border-rose-100' : 
                  'bg-amber-50 text-amber-600 border-amber-100'
                }`}>
                  {selectedCase.severity} Severity
                </span>
              </div>
              <p className="text-sm text-slate-500 font-medium">Monitoring Pattern: {selectedCase.type}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button className="px-5 py-2.5 bg-blue-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 shadow-xl shadow-blue-500/20 transition-all active:scale-95">
              Resolve Investigation
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-10 border-b border-slate-200">
          {[
            { id: 'overview', label: 'Case Overview' },
            { id: 'intelligence', label: 'Signal Analysis' },
            { id: 'history', label: 'Case History' }
          ].map(tab => (
            <button 
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`pb-4 text-[11px] font-black uppercase tracking-widest transition-all relative ${activeTab === tab.id ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
            >
              {tab.label}
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-full" />}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {activeTab === 'overview' && (
            <>
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
                  <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-8 flex items-center">
                    <FileText size={14} className="mr-2" /> Forensic Summary
                  </h3>
                  <div className="space-y-8">
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Narrative Description</p>
                      <p className="text-sm text-slate-900 font-medium leading-relaxed bg-slate-50 p-6 rounded-2xl border border-slate-100">
                        {selectedCase.description}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-8">
                      <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Target Identity</p>
                        <p className="text-sm font-mono text-slate-700 p-2.5 bg-white border border-slate-200 rounded-xl shadow-inner inline-block">
                          {selectedCase.targetAccount}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Current Custodian</p>
                        <div className="flex items-center space-x-2">
                           <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedCase.assignedTo}`} className="w-8 h-8 rounded-full bg-slate-100" alt="Officer" />
                           <p className="text-sm font-black text-slate-900">{selectedCase.assignedTo}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                 <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white shadow-2xl relative overflow-hidden group">
                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center">
                      <Zap size={16} className="mr-2 text-blue-400" /> Pattern Intelligence
                    </h3>
                    <div className="space-y-6 relative z-10">
                       <div className="flex justify-between items-baseline">
                          <span className="text-[10px] font-black text-slate-500 uppercase">Velocity Score</span>
                          <span className="text-3xl font-black text-rose-400">8.4x</span>
                       </div>
                       <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className="bg-rose-500 h-full w-[84%]"></div>
                       </div>
                       <p className="text-[10px] text-slate-400 font-medium leading-relaxed uppercase tracking-tight">
                         Signal detected across 14 linked nodes within a 72-hour window. Structured behavior confirmed.
                       </p>
                    </div>
                    <div className="absolute top-0 right-0 w-48 h-48 bg-blue-600 rounded-full blur-[80px] opacity-10 translate-x-1/2 -translate-y-1/2 group-hover:opacity-20 transition-opacity"></div>
                 </div>
              </div>
            </>
          )}

          {activeTab === 'history' && (
            <div className="lg:col-span-3 space-y-8 animate-in fade-in duration-500">
              <div className="bg-white p-10 rounded-[3rem] border border-slate-200 shadow-sm relative">
                <div className="flex items-center justify-between mb-12">
                  <div>
                    <h3 className="text-lg font-black text-slate-900 tracking-tight">Case Audit Timeline</h3>
                    <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Permanent immutable record of all case actions</p>
                  </div>
                  <div className="flex space-x-3">
                    <button className="p-3 bg-slate-50 border border-slate-100 rounded-2xl text-slate-400 hover:text-blue-600 transition-all hover:bg-white hover:shadow-md">
                      <Download size={20} />
                    </button>
                  </div>
                </div>

                <div className="relative space-y-12 before:absolute before:left-[19px] before:top-2 before:bottom-2 before:w-[2px] before:bg-slate-50">
                  {selectedCase.history.map((entry, idx) => (
                    <div key={entry.id} className="relative pl-14 animate-in fade-in slide-in-from-left duration-400" style={{ animationDelay: `${idx * 150}ms` }}>
                       <div className={`absolute left-0 top-1 w-10 h-10 rounded-2xl border-4 border-white z-10 flex items-center justify-center shadow-lg transition-transform hover:scale-110 ${
                         entry.type === 'system' ? 'bg-slate-900 text-blue-400' : 'bg-blue-600 text-white'
                       }`}>
                         {entry.type === 'system' ? <Zap size={14} /> : <User size={14} />}
                       </div>
                       
                       <div className={`p-6 rounded-[2rem] border transition-all ${
                         entry.type === 'system' ? 'bg-slate-50 border-slate-100' : 'bg-white border-blue-100 shadow-sm'
                       }`}>
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                             <div className="flex items-center space-x-3">
                                <span className={`text-[11px] font-black uppercase tracking-widest ${entry.type === 'system' ? 'text-slate-500' : 'text-blue-700'}`}>
                                  {entry.action}
                                </span>
                                {entry.statusChange && (
                                  <span className="text-[9px] font-black bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded uppercase tracking-widest border border-emerald-100">
                                    {" -> "} {entry.statusChange}
                                  </span>
                                )}
                             </div>
                             <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-white px-3 py-1 rounded-full border border-slate-100">{entry.date}</span>
                          </div>
                          
                          <div className="flex items-center space-x-4 mb-3">
                             <div className="flex items-center space-x-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                                <span className="text-slate-400 font-black">ACTOR:</span>
                                <span className="text-slate-900">{entry.user}</span>
                             </div>
                          </div>

                          {entry.notes && (
                            <div className="mt-4 p-5 bg-amber-50/50 rounded-2xl border border-amber-100/50 relative">
                               <p className="text-sm text-amber-900 font-medium italic leading-relaxed">"{entry.notes}"</p>
                               <div className="absolute -top-2 left-4 px-2 bg-amber-100 text-[8px] font-black text-amber-700 rounded-full uppercase tracking-widest border border-amber-200 shadow-sm">Internal Note</div>
                            </div>
                          )}
                       </div>
                    </div>
                  ))}
                  
                  {/* Real-time Note Input */}
                  <div className="relative pl-14 pt-6">
                    <div className="absolute left-0 top-7 w-10 h-10 rounded-2xl border-4 border-white z-10 bg-white shadow-xl flex items-center justify-center text-blue-600 animate-pulse">
                       <MessageSquare size={16} />
                    </div>
                    <div className="bg-blue-50/30 p-8 rounded-[2.5rem] border border-blue-100 border-dashed group focus-within:bg-white focus-within:border-blue-300 transition-all">
                       <div className="flex items-center space-x-4">
                          <input 
                            type="text" 
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                            placeholder="Add a case note or status update..." 
                            className="flex-1 bg-transparent outline-none text-sm font-bold text-slate-700 placeholder:text-slate-400 placeholder:uppercase placeholder:text-[10px] placeholder:tracking-widest"
                          />
                          <button 
                            onClick={handleAddNote}
                            disabled={!newNote.trim()}
                            className="p-3 bg-blue-600 text-white rounded-2xl shadow-lg shadow-blue-500/30 hover:bg-blue-700 disabled:opacity-30 transition-all active:scale-90"
                          >
                             <Send size={18} />
                          </button>
                       </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-in slide-in-from-right duration-500">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-600">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">AML Case Management</h1>
            <p className="text-sm text-slate-500 font-medium tracking-tight">Investigate structured transaction signals & sanctions nexus.</p>
          </div>
        </div>
        <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-2xl border border-slate-200 w-96 shadow-inner">
          <Search size={16} className="text-slate-400" />
          <input type="text" placeholder="Search case registry..." className="text-sm outline-none w-full font-medium" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 space-y-4">
           <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm">
             <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Queue Composition</h3>
             <div className="space-y-5">
               {[
                 { label: 'Structuring', count: 8, color: 'bg-rose-500' },
                 { label: 'High Velocity', count: 12, color: 'bg-blue-500' },
                 { label: 'Sanction Hit', count: 2, color: 'bg-slate-900' },
                 { label: 'PEP Signal', count: 3, color: 'bg-amber-500' },
               ].map((seg, i) => (
                 <div key={i} className="flex flex-col space-y-1.5">
                   <div className="flex justify-between text-[11px] font-black uppercase tracking-tight">
                     <span className="text-slate-500">{seg.label}</span>
                     <span className="text-slate-900">{seg.count}</span>
                   </div>
                   <div className="w-full bg-slate-100 h-1 rounded-full overflow-hidden">
                     <div className={`${seg.color} h-full rounded-full transition-all duration-1000`} style={{ width: `${(seg.count/25)*100}%` }}></div>
                   </div>
                 </div>
               ))}
             </div>
           </div>
        </div>

        <div className="lg:col-span-3 bg-white rounded-[2.5rem] border border-slate-200 shadow-sm flex flex-col min-h-[500px] overflow-hidden">
          <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/40">
             <div className="flex items-center space-x-6">
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">Ongoing Investigations</h3>
                <div className="flex space-x-2">
                  <span className="text-[10px] bg-white border border-slate-200 px-3 py-1.5 rounded-full font-black text-slate-600 uppercase tracking-widest shadow-sm">Total: 25</span>
                  <span className="text-[10px] bg-rose-50 border border-rose-100 px-3 py-1.5 rounded-full font-black text-rose-600 uppercase tracking-widest shadow-sm">Critical: 4</span>
                </div>
             </div>
             <button className="p-2.5 border border-slate-200 rounded-xl hover:bg-white hover:shadow-sm text-slate-400 hover:text-slate-900 transition-all shadow-sm">
                <Filter size={16} />
             </button>
          </div>
          
          <div className="divide-y divide-slate-100">
            {MOCK_CASES.map((item) => (
              <div 
                key={item.id} 
                onClick={() => setSelectedCase(item)}
                className="p-8 hover:bg-slate-50/80 cursor-pointer transition-all flex items-center justify-between group"
              >
                 <div className="flex items-start space-x-6">
                   <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all shadow-sm ${
                     item.severity === 'Critical' ? 'bg-rose-50 text-rose-500 group-hover:bg-rose-500 group-hover:text-white' : 'bg-slate-50 text-slate-400 group-hover:bg-blue-600 group-hover:text-white'
                   }`}>
                     <ShieldAlert size={28} />
                   </div>
                   <div>
                     <div className="flex items-center space-x-4 mb-1.5">
                       <span className="text-base font-black text-slate-900 group-hover:text-blue-600 transition-colors tracking-tight uppercase">{item.id}: {item.type}</span>
                       <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase border tracking-widest ${
                         item.severity === 'Critical' ? 'bg-rose-50 text-rose-700 border-rose-100' : 
                         'bg-amber-50 text-amber-700 border-amber-100'
                       }`}>
                         {item.severity} Risk
                       </span>
                     </div>
                     <p className="text-sm text-slate-500 mb-4 font-medium leading-relaxed max-w-xl">{item.description}</p>
                     <div className="flex items-center space-x-6 text-[10px] font-black uppercase tracking-widest text-slate-400">
                       <span className="flex items-center"><Clock size={14} className="mr-2" /> Detected {item.lastSeen}</span>
                       <span className="flex items-center"><User size={14} className="mr-2" /> {item.assignedTo}</span>
                       <span className="flex items-center space-x-2">
                          <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                          <span>{item.status}</span>
                       </span>
                     </div>
                   </div>
                 </div>
                 <div className="opacity-0 group-hover:opacity-100 transition-all translate-x-4 group-hover:translate-x-0">
                    <button className="p-3 bg-blue-600 text-white rounded-2xl shadow-xl shadow-blue-500/20 hover:bg-blue-700 transition-all">
                      <Eye size={20} />
                    </button>
                 </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
